import { useQueryClient } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { HelpCircle } from "lucide-react";
import { useCallback, useEffect } from "react";
import { z } from "zod";

import { FieldErrorsProvider } from "~/components/form/fields/field-errors";
import { ProfileEditHeader } from "~/components/profile-edit-header";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  UnsavedChangesAlert,
  useUnsavedChangesAlert,
} from "~/components/unsaved-changes-alert";
import { useCoachPrivateProfile } from "~/contexts/coach";
import { requireClerkCoachAuth } from "~/lib/coach-middleware";
import { Feature, hasFeature } from "~/lib/has-feature";
import { OfferType } from "~/lib/schemas/db-enums";
import { OfferSchema } from "~/lib/schemas/offer";
import type { OfferEditData, OfferFormData } from "~/lib/schemas/offer";

import { OfferErrorSummary } from "./offer/offer-error-summary";
import { OfferStatusControl } from "./offer/offer-status-control";
import { AboutSection } from "./offer/sections/about-section";
import { BenefitsSection } from "./offer/sections/benefits-section";
import { BonusesSection } from "./offer/sections/bonuses-section";
import { ChallengesSection } from "./offer/sections/challenges-section";
import { CurriculumSection } from "./offer/sections/curriculum-section";
import { FaqsSection } from "./offer/sections/faqs-section";
import { FinalCtaSection } from "./offer/sections/final-cta-section";
import { GuaranteeSection } from "./offer/sections/guarantee-section";
import { PricingSection } from "./offer/sections/pricing-section";
import { ProgramSection } from "./offer/sections/program-section";
import { ResultsSection } from "./offer/sections/results-section";
import { SummitSection } from "./offer/sections/summit-section";
import { TagsSection } from "./offer/sections/tags-section";
import { TestimonialsSection } from "./offer/sections/testimonials-section";
import { useEntityForm } from "./offer/use-entity-form";

export const getOfferData = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .validator((data: unknown) =>
    z.object({ offerId: z.string().optional() }).parse(data),
  )
  .handler(async ({ data, context }): Promise<OfferEditData> => {
    const { loadOfferEditData } = await import("~/lib/server/offer-edit-data");
    return loadOfferEditData(context.coach.id, data.offerId);
  });

export const saveOffer = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator((data: unknown) =>
    z
      .object({
        values: OfferSchema,
        offerId: z.string().optional(),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    const targetOfferId = data.offerId || data.values.id;
    if (!targetOfferId) {
      throw new Error("Offer ID is required");
    }

    const { saveCoachOffer } = await import("~/lib/server/offer-editor");
    const { loadOfferEditData } = await import("~/lib/server/offer-edit-data");
    await saveCoachOffer({
      coachId: context.coach.id,
      offerId: targetOfferId,
      values: data.values,
    });

    return {
      status: "success",
      message: "Offer saved",
      data: await loadOfferEditData(context.coach.id, targetOfferId),
    };
  });

export function OfferForm({
  initialData,
  title,
  titleAs,
  showHeader = true,
  onSave,
}: {
  initialData: OfferEditData;
  title: string;
  titleAs?: "h1" | "h2";
  showHeader?: boolean;
  onSave?: (values: OfferFormData) => Promise<OfferFormData>;
}) {
  const coach = useCoachPrivateProfile();
  const canUseOfferPage = coach ? hasFeature(coach, Feature.OfferPage) : false;
  const queryClient = useQueryClient();

  const availableSummits = coach?.summitOfferSummits ?? [];
  const activeWorkshops = coach?.activeSummitWorkshops ?? [];
  const summitLocked = Boolean(initialData.summitId);
  const summitSessionLocked = Boolean(initialData.summitSessionId);

  const defaultSave = useCallback(
    async (values: OfferFormData): Promise<OfferFormData> => {
      const res = await saveOffer({
        data: { values, offerId: values.id },
      });
      await queryClient.invalidateQueries({ queryKey: ["coach"] });
      return res.data;
    },
    [queryClient],
  );
  const save = onSave ?? defaultSave;

  const {
    form,
    values,
    isDirty,
    isSubmitting,
    submitAttempted,
    parsed,
    errorFor,
    handleSubmit,
  } = useEntityForm({
    initialData,
    save,
    successMessage: "Offer saved",
  });

  const isSummitOffer = values.type === OfferType.Summit;

  // Cross-cutting summit effects: lock the offer type + platform fee, and
  // reconcile the chosen session against the selected summit.
  useEffect(() => {
    if (!isSummitOffer) {
      return;
    }
    const lockedPlatformFee = initialData.platformFeeBps ?? 3000;
    form.setFieldValue("type", OfferType.Summit, { dontUpdateMeta: true });
    form.setFieldValue("platformFeeBps", lockedPlatformFee, {
      dontUpdateMeta: true,
    });
  }, [form, initialData.platformFeeBps, isSummitOffer]);

  useEffect(() => {
    if (!isSummitOffer || !values.summitId) {
      return;
    }
    const summitSessions = activeWorkshops.filter(
      (workshop) => workshop.session.summitId === values.summitId,
    );
    if (summitSessions.length === 0) {
      return;
    }
    const currentSessionId = form.state.values.summitSessionId;
    if (
      currentSessionId &&
      !summitSessions.some(
        (workshop) => workshop.session.id === currentSessionId,
      )
    ) {
      form.setFieldValue("summitSessionId", null);
    }
  }, [activeWorkshops, form, isSummitOffer, values.summitId]);

  const { showExitAlert, onDiscard, onCancel } = useUnsavedChangesAlert(
    () => form.state.isDirty,
  );

  const formDescription = isSummitOffer
    ? "Refine the assets for your summit special offer."
    : "Edit your offer. Clients see it once it's published.";

  return (
    <FieldErrorsProvider value={errorFor}>
      <UnsavedChangesAlert
        show={showExitAlert}
        onDiscard={onDiscard}
        onCancel={onCancel}
      />
      <form onSubmit={handleSubmit} className="space-y-8">
        {showHeader && (
          <ProfileEditHeader
            title={title}
            titleAs={titleAs}
            description={formDescription}
            username={values.username}
            previewOfferId={values.id || undefined}
            isDirty={isDirty}
            isSaving={isSubmitting}
            hasErrors={submitAttempted && !parsed.success}
          />
        )}

        {/* Summit specials go live through their summit, not the lifecycle. */}
        {!isSummitOffer && (
          <OfferStatusControl
            key={initialData.id}
            offerId={initialData.id}
            offerType={initialData.type}
            initialStatus={initialData.status}
            initialIsMain={initialData.isMain}
            hasUnsavedChanges={isDirty}
          />
        )}

        {submitAttempted && !parsed.success && (
          <OfferErrorSummary issues={parsed.error.issues} />
        )}

        {canUseOfferPage && (
          <Card>
            <CardHeader>
              <CardTitle className="flex gap-2">
                <HelpCircle className="size-[1em] text-brand-700" /> How your
                offer is presented
              </CardTitle>
            </CardHeader>
            <CardContent className="prose">
              <p>
                On your main profile page, we show your offer <em>headline</em>,{" "}
                <em>description</em> and an overview of the <em>benefits</em>{" "}
                (just the titles).
              </p>
              <p>
                The rest of the details are shown on the full offer page, which
                has the following structure:
              </p>
              <ul>
                <li>About</li>
                <li>Tags</li>
                <li>Challenges / Struggles</li>
                <li>Results from following this program</li>
                <li>About the Program (practical details)</li>
                <li>Program structure (a module-by-module plan)</li>
                <li>Benefits of this program</li>
                <li>Testimonials</li>
                <li>Bonuses</li>
                <li>Satisfaction Guarantee</li>
                <li>FAQs</li>
                <li>Final call to action</li>
              </ul>
              <p>
                You're free to leave fields empty when you don't want that
                section to be visible on the offer page.
              </p>
            </CardContent>
          </Card>
        )}

        {isSummitOffer && (
          <SummitSection
            form={form}
            availableSummits={availableSummits}
            activeWorkshops={activeWorkshops}
            summitLocked={summitLocked}
            summitSessionLocked={summitSessionLocked}
            platformFeeBps={initialData.platformFeeBps ?? null}
          />
        )}

        <AboutSection form={form} canUseOfferPage={canUseOfferPage} />
        <PricingSection form={form} />

        {canUseOfferPage && <ChallengesSection form={form} />}
        {canUseOfferPage && <ResultsSection form={form} />}
        {canUseOfferPage && <ProgramSection form={form} />}
        {canUseOfferPage && (
          <CurriculumSection
            form={form}
            showUnitSelector
            title="Program structure"
            description="Break your program into an ordered plan — the modules, weeks, or phases clients move through. Shown on your offer page; leave empty to hide it."
          />
        )}

        <TagsSection form={form} />
        <BenefitsSection form={form} canUseOfferPage={canUseOfferPage} />

        {canUseOfferPage && <GuaranteeSection form={form} />}

        <TestimonialsSection form={form} />

        {canUseOfferPage && <BonusesSection form={form} />}

        <FaqsSection form={form} />
        <FinalCtaSection form={form} />
      </form>
    </FieldErrorsProvider>
  );
}
